/**
 * yojo-mix-box.js — Type C build-a-box PDP runtime (PDP system phase 1, 2026-09-02).
 * Spec: docs/specs/2026-09-02-pdp-system-design.md §3.1 · plan Task 5.
 *
 * Reads the JSON island rendered by snippets/yojo-mix-box.liquid (strings +
 * per-flavor data), drives Step 1 (mode), Step 2 (flavor steppers), the
 * counter / progress / CTA, and adds ONE cart line with `_mix:N` properties
 * that extensions/mix-box-expand (Cart Transform) expands at checkout.
 *
 * No Hebrew constants here: every visible string comes from the island.
 * Depends on assets/yojo-mix-box-state.js (window.YojoMixBoxState).
 */
(function () {
  'use strict';

  // 2026-10-02 (swarm vf-K7, T-S14): whole shekels, as Liquid's money_without_trailing_zeros prints
  // them on this page and on the cards (26 Sep ruling): "110 ₪", "11 ₪ לארטיק"; "16.50 ₪" keeps agorot.
  function money(cents, fmt) {
    var n = Math.round(cents) / 100;
    var v = n % 1 === 0 ? String(n) : n.toFixed(2);
    if (fmt && fmt.indexOf('{{') !== -1) return fmt.replace(/\{\{\s*amount[^}]*\}\}/, v);
    return v + ' ₪';
  }
  // Data in the island is merchant-authored (product titles, ingredient
  // lists) and already JSON-escaped by Liquid; escape again before any
  // innerHTML so a stray "<" in an ingredient list can never become markup.
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function tpl(s, map) {
    return Object.keys(map).reduce(function (acc, k) { return acc.split('%' + k + '%').join(map[k]); }, s || '');
  }

  // 2026-10-02 (swarm b3-pdp; a11y ruling R8, "one message per add"). The add used to write
  // the button label ("הוספה לסל") to the live region, and yojo-a11y-cart-announcer.js then
  // said "נוסף לסל: <box>." from the cart diff: two messages, the first one wrong. The
  // announcer now speaks alone; this only speaks (the island's "added" line) if no cart line
  // reaches the live region within 3 s, so the add is still announced when the announcer's
  // /cart.js read fails.
  // 2026-10-02 (swarm f3-pdp, r3 review): the watch starts when the add is sent, not when its
  // answer arrives. On a slow answer the announcer spoke first (measured 1.2 s early), a watch
  // that started late missed it, and the fallback then said a second line. Only a cart line
  // counts (the same test as yojo-pdp-v2.js), not any text in the region.
  var CART_LINE = /^(נוסף לסל|נוספה לסל|עודכנה כמות|הסל עודכן)/;
  function watchCartLine() {
    var sr = document.getElementById('screen-reader-info');
    var heard = false, mo = null;
    if (sr && window.MutationObserver) {
      mo = new MutationObserver(function () { if (CART_LINE.test((sr.textContent || '').trim())) heard = true; });
      mo.observe(sr, { childList: true, characterData: true, subtree: true });
    }
    function stop() { if (mo) { mo.disconnect(); mo = null; } }
    return {
      stop: stop,
      // After a successful add: speak `text` only if no cart line has been heard 3 s from now.
      settle: function (text) {
        if (!sr || !text) { stop(); return; }
        setTimeout(function () {
          stop();
          if (heard) return;
          sr.textContent = '';
          setTimeout(function () {
            sr.textContent = text;
            setTimeout(function () { if (sr.textContent === text) sr.textContent = ''; }, 4000);
          }, 30);
        }, 3000);
      }
    };
  }

  function init(root) {
    var island = root.querySelector('[data-mix-json]');
    var State = window.YojoMixBoxState;
    if (!island || !State) return;
    var data;
    try { data = JSON.parse(island.textContent); } catch (e) { return; }
    var S = data.strings;
    var fmt = root.getAttribute('data-money-format');
    var step = parseInt(root.getAttribute('data-step'), 10) || 2;
    var sizes = { small: parseInt(root.getAttribute('data-size-small'), 10), large: parseInt(root.getAttribute('data-size-large'), 10) };
    var variants = { small: root.getAttribute('data-variant-small'), large: root.getAttribute('data-variant-large') };
    var prices = { small: parseInt(root.getAttribute('data-price-small'), 10), large: parseInt(root.getAttribute('data-price-large'), 10) };
    var flavorsByHandle = {};
    data.flavors.forEach(function (f) { flavorsByHandle[f.handle] = f; });

    var el = {
      modes: root.querySelectorAll('[data-mix-mode]'),
      gridWrap: root.querySelector('[data-mix-grid-wrap]'),
      tiles: Array.prototype.slice.call(root.querySelectorAll('[data-mix-tile]')),
      counter: root.querySelector('[data-mix-counter]'),
      modeName: root.querySelector('[data-mix-mode-name]'),
      algUnion: root.querySelector('[data-mix-alg-union]'),
      progress: root.querySelector('[data-mix-progress]'),
      progressWrap: root.querySelector('.yojo-mb__progress'),
      note: root.querySelector('[data-mix-note]'),
      total: root.querySelector('[data-mix-total]'),
      qty: root.querySelector('[data-mix-qty]'),
      qtyDec: root.querySelector('[data-mix-qty-dec]'),
      qtyInc: root.querySelector('[data-mix-qty-inc]'),
      form: root.querySelector('[data-mix-form]'),
      formId: root.querySelector('[data-mix-form-id]'),
      formQty: root.querySelector('[data-mix-form-qty]'),
      formProps: root.querySelector('[data-mix-form-props]'),
      cta: root.querySelector('[data-mix-cta]'),
      ctaLabel: root.querySelector('[data-mix-cta] .yojo-pdp__atc-label'),
      error: root.querySelector('[data-mix-error]'),
      facts: root.querySelector('[data-mix-facts-body]'),
      single: root.querySelector('[data-mix-single]'),
      singlePacks: root.querySelector('[data-mix-single-packs]'),
      singleFrom: root.querySelector('[data-mix-single-from]'),
      priceNow: root.closest('product-page') ? root.closest('product-page').querySelector('[data-js-pdp-price-now]') : null,
      perUnit: root.closest('product-page') ? root.closest('product-page').querySelector('[data-mix-per-unit]') : null
    };

    var mode = 'mixed-small';
    var box = State.createBoxState({ size: sizes.small, step: step });
    var singleFlavor = null;
    var singleVariant = null;
    var pendingShrink = null;

    // ---------- helpers ----------
    function tileFor(handle) {
      return el.tiles.filter(function (t) { return t.getAttribute('data-handle') === handle; })[0];
    }
    function qty() { return Math.max(1, parseInt(el.qty.value, 10) || 1); }
    function currentKey() { return mode === 'mixed-large' ? 'large' : 'small'; }
    function boxPrice() { return prices[currentKey()]; }
    function unitPrice(cents, n) { return money(Math.round(cents / n), fmt); }
    function setNote(html, cls) {
      if (!html) { el.note.hidden = true; el.note.innerHTML = ''; el.note.className = 'yojo-mb__note'; summaryChanged(); return; }
      el.note.hidden = false; el.note.className = 'yojo-mb__note' + (cls ? ' ' + cls : ''); el.note.innerHTML = html;
      summaryChanged();
    }
    function shake(tile) {
      if (!tile) return;
      tile.classList.remove('is-shake'); void tile.offsetWidth; tile.classList.add('is-shake');
    }
    function setHeaderPrice(cents, n) {
      if (el.priceNow) el.priceNow.textContent = money(cents, fmt);
      if (el.perUnit && n) el.perUnit.textContent = tpl(S.per_unit, { AMOUNT: unitPrice(cents, n) });
    }

    // ---------- render ----------
    function renderTiles() {
      el.tiles.forEach(function (t) {
        var h = t.getAttribute('data-handle');
        var c = box.get(h);
        var out = t.querySelector('[data-mix-count]');
        if (out) out.textContent = String(c);
        // A7-08 (2026-09-03): the box's per-flavor counts survive a switch to
        // "single flavour" mode (mode === 'single' has no "box" concept), so
        // tiles kept showing has-picks styling from whatever was picked while
        // in mixed mode. has-picks only means something in mixed mode.
        t.classList.toggle('has-picks', mode !== 'single' && c > 0);
        var avail = t.getAttribute('data-available') === 'true';
        var plus = t.querySelector('[data-mix-plus]');
        var minus = t.querySelector('[data-mix-minus]');
        if (plus) plus.disabled = !avail || !box.canAdd(h);
        if (minus) minus.disabled = c === 0;
      });
    }
    function renderSummary() {
      var isMix = mode === 'mixed-small' || mode === 'mixed-large';
      var total;
      // F-5 (visual QA 2026-10-21): the sticky summary names the box being
      // built — the checked mode card's own title, so no copy is duplicated.
      if (el.modeName) {
        var checkedMode = root.querySelector('[data-mix-mode]:checked');
        var modeLabel = checkedMode && checkedMode.closest('label');
        var modeTitle = modeLabel && modeLabel.querySelector('.yojo-mb__mode-title');
        el.modeName.textContent = modeTitle ? modeTitle.textContent.trim() : '';
      }
      if (isMix) {
        var picked = box.picked(), size = box.size, rem = box.remaining();
        el.counter.textContent = tpl(S.counter, { PICKED: picked, SIZE: size });
        el.progress.style.width = Math.round((picked / size) * 100) + '%';
        el.progressWrap.classList.toggle('is-full', rem === 0);
        total = boxPrice() * qty();
        el.total.textContent = money(total, fmt);
        setHeaderPrice(boxPrice(), size);
        if (rem === 0) {
          setCta(false, tpl(S.cta_full, { PRICE: money(total, fmt) }));
        } else if (picked === 0) {
          setCta(true, tpl(S.cta_empty, { COUNT: size }));
        } else {
          setCta(true, tpl(S.cta_more, { COUNT: rem }));
        }
        renderAllergenUnion();
        renderFacts();
      } else if (mode === 'single') {
        el.counter.textContent = singleFlavor ? flavorsByHandle[singleFlavor].name : S.single_choose;
        el.progress.style.width = singleVariant ? '100%' : '0%';
        el.progressWrap.classList.toggle('is-full', !!singleVariant);
        el.algUnion.textContent = '';
        if (singleVariant) {
          total = singleVariant.price * qty();
          el.total.textContent = money(total, fmt);
          setHeaderPrice(singleVariant.price, singleVariant.qty);
          setCta(!singleVariant.available, singleVariant.available ? tpl(S.cta_full, { PRICE: money(total, fmt) }) : S.sold_out);
        } else {
          el.total.textContent = '';
          setCta(true, S.single_choose);
          // A7-08 (2026-09-03): before a flavour/pack is chosen, the H1 price
          // used to stay pinned to whatever the LAST mixed-box price was (it is
          // only ever written by setHeaderPrice, and single mode with no
          // variant never called it). Reset to the single-tier "from" price the
          // mode card itself already computed and shows (el.singleFrom).
          if (el.priceNow && el.singleFrom) el.priceNow.textContent = el.singleFrom.textContent;
        }
        renderFacts();
      } else if (mode === 'sampler') {
        var sp = data.sampler;
        el.counter.textContent = sp ? sp.title : '';
        el.progress.style.width = '100%';
        el.progressWrap.classList.add('is-full');
        el.algUnion.textContent = '';
        total = sp ? sp.price * qty() : 0;
        el.total.textContent = money(total, fmt);
        if (sp) setHeaderPrice(sp.price, sizes.small);
        setCta(!(sp && sp.available), sp && sp.available ? tpl(S.cta_full, { PRICE: money(total, fmt) }) : S.sold_out);
        renderFacts();
      }
      summaryChanged();
    }
    function setCta(disabled, label) {
      el.cta.disabled = disabled;
      el.cta.setAttribute('aria-disabled', String(disabled));
      el.ctaLabel.textContent = label;
    }
    function chosenFlavors() {
      if (mode === 'single') return singleFlavor ? [{ handle: singleFlavor, count: singleVariant ? singleVariant.qty : 0 }] : [];
      if (mode === 'sampler') return [];
      return box.entries();
    }
    function unionAllergens(entries) {
      var set = [], dairy = false, unknown = false;
      entries.forEach(function (e) {
        var f = flavorsByHandle[e.handle]; if (!f) return;
        if (f.allergens === null) unknown = true;
        (f.allergens || []).forEach(function (a) { if (set.indexOf(a) === -1) set.push(a); });
        if (f.kosher === S.dairy || (f.allergens || []).some(function (a) { return a.indexOf('חלב') !== -1; })) dairy = true;
      });
      return { list: set, dairy: dairy, unknown: unknown };
    }
    // Semantic integrity 2026-09-04. "מכיל: X" states that the thing you are buying
    // contains X. In a build-your-own box the list is a UNION across the flavours the
    // customer picked, so a box holding one milk flavour and five without would read as
    // though every stick contained milk.
    //
    // CORRECTED same day, after the pre-deploy sweep. The first version hedged on the
    // COUNT of flavours picked, which is wrong in the dangerous direction: pick
    // coconut-vegan x6 + strawberry-cream x4 — both declare exactly ["סויה"] — and it
    // said "מכיל: סויה · לא כל טעם מכיל את כולם" when in fact EVERY stick in that box
    // contains soy. A soy-avoiding customer could read that as permission to eat some
    // of them. Reproduced on three real selections across both mix boxes.
    //
    // The hedge belongs to UNEVENNESS, not to plurality: emit it only when some picked
    // flavour is missing at least one allergen that the union names. A flavour whose
    // allergens are unrecorded counts as missing them — unknown is not "contains", and
    // hedging is the honest reading there.
    function allergensAreUneven(entries, u) {
      var picked = [];
      entries.forEach(function (e) {
        if (e && e.count > 0 && flavorsByHandle[e.handle]) picked.push(flavorsByHandle[e.handle]);
      });
      if (picked.length < 2) return false;
      return picked.some(function (f) {
        var own = f.allergens || [];
        return u.list.some(function (a) { return own.indexOf(a) === -1; });
      });
    }
    // Order matters: the hedge goes LAST. Built the other way round it produced
    // "מכיל: X · לא כל טעם מכיל את כולם · ללא חלב", where an absolute claim sits behind
    // a qualifier and can be read as qualified by it. "ללא חלב" is unconditional and
    // must not look hedged.
    function containsLine(entries, u) {
      if (!u.list.length) return u.unknown ? '' : S.no_allergens;
      var txt = tpl(S.contains, { LIST: u.list.join(', ') });
      if (!u.dairy) txt += ' · ' + S.no_dairy;
      if (allergensAreUneven(entries, u)) txt += ' · ' + S.allergen_varies;
      return txt;
    }
    function renderAllergenUnion() {
      var u = unionAllergens(box.entries());
      if (box.picked() === 0) { el.algUnion.textContent = ''; return; }
      el.algUnion.textContent = containsLine(box.entries(), u);
    }
    function renderFacts() {
      var entries = chosenFlavors();
      if (!entries.length) { el.facts.innerHTML = '<p class="yojo-mb__facts-empty">' + esc(S.facts_empty) + '</p>'; return; }
      var u = unionAllergens(entries);
      var html = '';
      // A3-10 (2026-09-03): "מה במארז שלכם" opened straight into the allergen
      // union — the actual answer to "what's in your box" (which flavours, how
      // many of each) only showed up further down, one paragraph per flavor
      // mixed in with ingredients. Render the compact "name ×count" list first,
      // from the same `entries` the union itself is built from.
      var flavorSummary = entries.map(function (e) {
        var f = flavorsByHandle[e.handle];
        return f ? esc(f.name) + (e.count ? ' ×' + e.count : '') : '';
      }).filter(Boolean).join(' · ');
      if (flavorSummary) html += '<p class="yojo-mb__fact-summary">' + flavorSummary + '</p>';
      var unionTxt = containsLine(entries, u);
      if (unionTxt) html += '<p class="yojo-mb__fact-union">' + esc(unionTxt) + '</p>';
      entries.forEach(function (e) {
        var f = flavorsByHandle[e.handle]; if (!f) return;
        html += '<p class="yojo-mb__fact"><b>' + esc(f.name) + (e.count ? ' ×' + e.count : '') + (f.kosher ? ' · ' + esc(f.kosher) : '') + '</b>' + esc(f.ingredients || '') + '</p>';
      });
      el.facts.innerHTML = html;
    }
    function renderMode() {
      root.classList.toggle('yojo-mb--single', mode === 'single');
      root.classList.toggle('yojo-mb--sampler', mode === 'sampler');
      el.single.hidden = mode !== 'single';
      el.tiles.forEach(function (t) { t.classList.toggle('is-selected', mode === 'single' && t.getAttribute('data-handle') === singleFlavor); });
      renderTiles();
      renderSummary();
    }
    function renderSinglePacks() {
      el.singlePacks.innerHTML = '';
      singleVariant = null;
      if (!singleFlavor) return;
      var f = flavorsByHandle[singleFlavor];
      f.variants.forEach(function (v, i) {
        var lab = document.createElement('label');
        lab.className = 'yojo-mb__single-pack';
        lab.innerHTML = '<input type="radio" name="mix-single-pack" class="visually-hidden" value="' + parseInt(v.id, 10) + '"' + (v.available ? '' : ' disabled') + '><b>' + esc(v.title) + '</b><small>' + esc(money(v.price, fmt)) + ' · ' + esc(tpl(S.per_unit, { AMOUNT: unitPrice(v.price, v.qty) })) + '</small>';
        var input = lab.querySelector('input');
        input.addEventListener('change', function () { singleVariant = v; renderSummary(); });
        el.singlePacks.appendChild(lab);
        if (v.available && singleVariant === null) { input.checked = true; singleVariant = v; }
      });
    }

    // ---------- events: Step 1 ----------
    Array.prototype.forEach.call(el.modes, function (r) {
      r.addEventListener('change', function () {
        if (!r.checked) return;
        var next = r.value;
        setNote('');
        if (next === 'mixed-small' || next === 'mixed-large') {
          var target = sizes[next === 'mixed-large' ? 'large' : 'small'];
          if (target < box.picked() && !pendingShrink) {
            // confirm before trimming
            pendingShrink = next;
            // A7-02/A7-03 (2026-09-03): this was a single 32×24 "✓" icon button with
            // no cancel — the only way out was reloading the page, and confirming
            // never re-synced the mode radios (renderMode() ran against the OLD
            // `mode`, so the visible selection could disagree with the box size
            // that had just been trimmed). Two real ≥44px text buttons now, and the
            // confirm handler re-checks the radios before rendering.
            setNote(
              esc(tpl(S.switch_down_confirm, { SIZE: target })) +
                ' <button type="button" class="yojo-mb__shrink-confirm" data-mix-confirm-shrink>' +
                esc(tpl(S.shrink_confirm, { COUNT: target })) +
                '</button>' +
                '<button type="button" class="yojo-mb__shrink-cancel" data-mix-cancel-shrink>' +
                esc(S.shrink_cancel) +
                '</button>',
              'yojo-mb__note--confirm'
            );
            var confirmBtn = el.note.querySelector('[data-mix-confirm-shrink]');
            var cancelBtn = el.note.querySelector('[data-mix-cancel-shrink]');
            confirmBtn.addEventListener('click', function () {
              pendingShrink = null;
              mode = next;
              box.setSize(target);
              setNote('');
              Array.prototype.forEach.call(el.modes, function (x) { x.checked = x.value === mode; });
              renderMode();
            });
            cancelBtn.addEventListener('click', function () {
              pendingShrink = null;
              setNote('');
              Array.prototype.forEach.call(el.modes, function (x) { x.checked = x.value === mode; });
            });
            // keep the previous radio visually until confirmed
            Array.prototype.forEach.call(el.modes, function (x) { x.checked = x.value === mode; });
            return;
          }
          pendingShrink = null;
          var wasPicked = box.picked();
          mode = next;
          box.setSize(target);
          if (target > wasPicked && wasPicked > 0) setNote(tpl(S.switch_up_note, { COUNT: box.remaining() }));
        } else {
          mode = next;
          if (mode === 'single') { renderSinglePacks(); }
        }
        renderMode();
      });
    });

    // ---------- events: Step 2 ----------
    el.tiles.forEach(function (t) {
      var h = t.getAttribute('data-handle');
      var plus = t.querySelector('[data-mix-plus]');
      var minus = t.querySelector('[data-mix-minus]');
      var info = t.querySelector('[data-mix-info]');
      var sheet = t.querySelector('[data-mix-sheet]');
      // A7-11 (2026-09-03): tapping "+" added 2 units at a time with no
      // indication, so the counter jump read as a double-tap bug. D-2
      // (2026-10-21): the label is now SERVER-rendered by yojo-mix-box.liquid,
      // inside <bdi dir="ltr"> — the plain textContent write here flipped to
      // "2+" in the RTL page (owner screenshot) and wiped that isolate, so it
      // must not run any more.
      if (plus) plus.addEventListener('click', function () {
        if (!box.canAdd(h)) { shake(t); setNote(S.box_full_note); return; }
        setNote('');
        box.add(h); renderTiles(); renderSummary();
      });
      if (minus) minus.addEventListener('click', function () { setNote(''); box.remove(h); renderTiles(); renderSummary(); });
      if (info && sheet) info.addEventListener('click', function () {
        var open = sheet.hidden; sheet.hidden = !open; info.setAttribute('aria-expanded', String(open));
      });
      t.addEventListener('click', function (e) {
        if (mode !== 'single') return;
        if (e.target.closest('[data-mix-info]')) return;
        if (t.getAttribute('data-available') !== 'true') return;
        singleFlavor = h;
        var radio = t.querySelector('[data-mix-single-radio]'); if (radio) radio.checked = true;
        renderSinglePacks();
        renderMode();
      });
    });

    // shortcuts
    Array.prototype.forEach.call(root.querySelectorAll('[data-mix-shortcut]'), function (b) {
      b.addEventListener('click', function () {
        var kind = b.getAttribute('data-mix-shortcut');
        setNote('');
        if (kind === 'clear') { box.clear(); renderTiles(); renderSummary(); return; }
        var pool = data.flavors.filter(function (f) {
          if (!f.available) return false;
          var t = tileFor(f.handle); if (!t) return false;
          if (kind === 'best') return f.bestseller;
          if (kind === 'fruity') return t.getAttribute('data-group') === 'fruity';
          if (kind === 'vegan') return t.getAttribute('data-vegan') === 'true';
          return true;
        }).map(function (f) { return f.handle; });
        if (kind === 'best' && pool.length === 0) pool = data.flavors.filter(function (f) { return f.available; }).map(function (f) { return f.handle; });
        // 2026-09-27 audit F3: with one in-stock bestseller the MOZ "mixed" box filled as 10 x
        // one flavour. A mix needs at least 3 flavours: top the bestseller pool up with the other
        // in-stock flavours, in catalogue order, bestsellers first.
        if (kind === 'best' && pool.length < 3) {
          data.flavors.forEach(function (f) {
            if (pool.length >= 3 || !f.available || pool.indexOf(f.handle) !== -1 || !tileFor(f.handle)) return;
            pool.push(f.handle);
          });
        }
        if (kind === 'fruity' || kind === 'vegan') box.clear();
        box.fill(pool);
        renderTiles(); renderSummary();
      });
    });

    // quantity
    function bumpQty(d) { el.qty.value = String(Math.min(10, Math.max(1, qty() + d))); renderSummary(); }
    if (el.qtyDec) el.qtyDec.addEventListener('click', function () { bumpQty(-1); });
    if (el.qtyInc) el.qtyInc.addEventListener('click', function () { bumpQty(1); });
    el.qty.addEventListener('change', function () { bumpQty(0); });

    // ---------- add to cart ----------
    function buildPayload() {
      var q = qty();
      if (mode === 'single') return singleVariant ? { items: [{ id: singleVariant.id, quantity: q }] } : null;
      if (mode === 'sampler') return data.sampler ? { items: [{ id: data.sampler.variantId, quantity: q }] } : null;
      if (!box.isFull()) return null;
      var props = { _mix_box: '1' };
      var human = [];
      box.entries().forEach(function (e, i) {
        var f = flavorsByHandle[e.handle];
        props['_mix:' + (i + 1)] = f.sourceGid + '|' + e.count;
        human.push(f.name + ' ×' + e.count);
      });
      props[S.flavors_prop] = human.join(' · ');
      return { items: [{ id: parseInt(variants[currentKey()], 10), quantity: q, properties: props }] };
    }

    el.form.addEventListener('submit', function (e) {
      e.preventDefault();
      var payload = buildPayload();
      if (!payload) { shake(root.querySelector('.yojo-mb__summary')); return; }
      el.error.hidden = true;
      el.cta.setAttribute('disabled', ''); el.cta.classList.add('is-working');
      window._yojoCartMutationsInFlight = (window._yojoCartMutationsInFlight || 0) + 1;
      // multipart FormData, exactly like yojo-pdp-v2.js: the JSON body variant of
      // /cart/add.js is bot-challenged in automation (tests/e2e/helpers.ts
      // seedCart note, measured 2026-08-26) while the form-encoded path passes.
      var fd = new FormData();
      var item = payload.items[0];
      // same envelope the theme's own product forms send (form_type + utf8)
      fd.append('form_type', 'product');
      fd.append('utf8', '\u2713');
      fd.append('id', String(item.id));
      fd.append('quantity', String(item.quantity));
      if (item.properties) Object.keys(item.properties).forEach(function (k) { fd.append('properties[' + k + ']', item.properties[k]); });
      // 2026-10-02 (swarm c06 finding 4): one round trip, as in yojo-pdp-v2.js. The add returns
      // the drawer section; the ?section_id=helper-cart GET is only the fallback.
      fd.append('sections', 'helper-cart');
      fd.append('sections_url', window.location.pathname);
      var added = false;
      var addWatch = watchCartLine();
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: fd
      })
        .then(function (res) { return res.text().then(function (b) { var j = null; try { j = JSON.parse(b); } catch (x) {} return { ok: res.ok, j: j }; }); })
        .then(function (r) {
          if (!r.ok) {
            var raw = (r.j && (r.j.description || r.j.message)) || '';
            throw new Error(/[֐-׿]/.test(raw) ? raw : S.add_error);
          }
          added = true;
          addWatch.settle(S.added);
          var html = r.j && r.j.sections && r.j.sections['helper-cart'];
          if (typeof html === 'string' && html) return html;
          return fetch('?section_id=helper-cart').then(function (x) { return x.text(); });
        })
        .then(function (text) {
          if (typeof text !== 'string') return;
          var doc = new DOMParser().parseFromString(text, 'text/html');
          var newCountEl = doc.querySelector('[data-cart-count]');
          var newTotalEl = doc.querySelector('[data-cart-total]');
          var newCountText = newCountEl ? newCountEl.textContent : null;
          var newTotalText = newTotalEl ? newTotalEl.textContent : null;
          var ajaxCartForm = document.getElementById('AjaxCartForm');
          var newInner = doc.getElementById('AjaxCartForm');
          if (ajaxCartForm && newInner) {
            ajaxCartForm.replaceChildren.apply(ajaxCartForm, Array.prototype.slice.call(newInner.childNodes));
            if (typeof ajaxCartForm.ajaxifyCartItems === 'function') ajaxCartForm.ajaxifyCartItems();
            if (typeof window.yojoReplaceCartSubtotal === 'function') window.yojoReplaceCartSubtotal(doc);
            else {
              var st = document.getElementById('AjaxCartSubtotal'), nst = doc.getElementById('AjaxCartSubtotal');
              if (st && nst) st.replaceChildren.apply(st, Array.prototype.slice.call(nst.childNodes));
            }
          }
          if (newCountText !== null) document.querySelectorAll('[data-header-cart-count]').forEach(function (n) { n.textContent = newCountText; });
          if (newTotalText !== null) document.querySelectorAll('[data-header-cart-total]').forEach(function (n) { n.textContent = newTotalText; });
          var siteCart = document.getElementById('site-cart-sidebar');
          if (siteCart && typeof siteCart.show === 'function') siteCart.show();
          // The suggestions rebuild once, from component-cart-recommendations.js on 'cart:updated'.
          document.dispatchEvent(new CustomEvent('cart:updated'));
          document.dispatchEvent(new CustomEvent('product:added'));
        })
        .catch(function (err) {
          // The box is already in the cart when only the drawer render failed: never say it was
          // not added (a retry would add a second box). Show the cart instead, as yojo-pdp-v2.js does.
          if (added) { window.location.assign('/cart'); return; }
          addWatch.stop();
          el.error.textContent = (err && err.message) || S.add_error;
          el.error.hidden = false;
        })
        .finally(function () {
          window._yojoCartMutationsInFlight = Math.max(0, (window._yojoCartMutationsInFlight || 1) - 1);
          el.cta.classList.remove('is-working');
          renderSummary();
        });
    });

    // ---------- sticky summary ----------
    // Two classes, one place that sets them (2026-10-02, swarm f3-pdp, r3 review P1/P2):
    // - .is-armed (desktop only; yojo-mix-box.css ignores it below 1024px, where the summary is
    //   always sticky): the summary sticks only once the box-size choices (Step 1) sit above the
    //   band the stuck summary takes at the foot of the screen (ARM_BAND, the compact summary's
    //   131px plus a margin), so it never covers them (a18-D1).
    // - .is-stuck (the compact form: counter, progress, button, minimum): only while the summary
    //   is sticky AND its full form would not fit at its own place in the page. The old rule
    //   ("the sentinel after the summary is below the screen") fed on itself: compacting the
    //   summary pulled the sentinel into view, which un-compacted it, which pushed the sentinel
    //   out again, every frame (23 to 32 class flips per 300 ms on laptops, the add button
    //   jumping 90px). Measuring the summary's own place (the sentinel minus the summary's
    //   current height) against its FULL height gives the same answer in both forms, so it
    //   settles. It also never compacts an unarmed (in-place) summary on desktop.
    var summary = root.querySelector('[data-mix-summary]');
    var sentinel = root.querySelector('[data-mix-sentinel]');
    var stepModes = root.querySelector('.yojo-mb__step--modes');
    var ARM_BAND = 160;
    var fullH = 0;
    var layoutRaf = 0;
    function measureFull() {
      if (!summary) return;
      // Same frame, no paint in between: the compact form never shows unstuck, or the reverse.
      var stuckNow = summary.classList.contains('is-stuck');
      if (stuckNow) summary.classList.remove('is-stuck');
      fullH = summary.offsetHeight;
      if (stuckNow) summary.classList.add('is-stuck');
    }
    function layoutSummary() {
      layoutRaf = 0;
      if (!summary || !sentinel) return;
      var vh = window.innerHeight;
      summary.classList.toggle('is-armed', !stepModes || stepModes.getBoundingClientRect().bottom <= vh - ARM_BAND);
      var cs = window.getComputedStyle(summary);
      var stuck = false;
      if (cs.position === 'sticky') {
        if (!summary.classList.contains('is-stuck') || !fullH) fullH = summary.offsetHeight;
        var ownTop = sentinel.getBoundingClientRect().top - summary.offsetHeight;
        stuck = ownTop + fullH > vh - (parseFloat(cs.bottom) || 0) + 1;
      }
      if (summary.classList.contains('is-stuck') !== stuck) {
        summary.classList.toggle('is-stuck', stuck);
        // The accessibility-button lift (yojo-pdp-sticky-atc.liquid) re-checks against the new form.
        summary.dispatchEvent(new CustomEvent('yojo:mix-summary-layout'));
      }
    }
    function queueSummaryLayout() { if (!layoutRaf) layoutRaf = window.requestAnimationFrame(layoutSummary); }
    // Content inside the summary changed (counts, note, mode): its full height may have changed.
    function summaryChanged() { if (!summary) return; measureFull(); layoutSummary(); }
    if (summary && sentinel) {
      window.addEventListener('scroll', queueSummaryLayout, { passive: true });
      window.addEventListener('resize', function () { measureFull(); queueSummaryLayout(); });
      window.addEventListener('load', queueSummaryLayout);
      // Layout shifts above the builder (fonts, images) move it without a scroll event.
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(queueSummaryLayout, { threshold: 0 }).observe(sentinel);
        if (stepModes) new IntersectionObserver(queueSummaryLayout, { rootMargin: '0px 0px -' + ARM_BAND + 'px 0px', threshold: [0, 1] }).observe(stepModes);
      }
    }

    // P2 (r3 review): with the sampler there is no flavour step, so on a laptop the summary sits
    // right under the box choices, in place (not armed), and its add button can start below the
    // fold (y 770 of 768 at 1366x768). When the shopper picks it, scroll just enough to show the
    // whole summary, never so far that the box choices go under the header.
    function revealSummary() {
      if (!summary || !el.cta) return;
      layoutSummary();
      if (window.getComputedStyle(summary).position === 'sticky') return; // already at the foot (phones, armed)
      var vh = window.innerHeight;
      if (el.cta.getBoundingClientRect().bottom <= vh - 8) return;
      var by = summary.getBoundingClientRect().bottom + 8 - vh;
      if (stepModes) by = Math.min(by, stepModes.getBoundingClientRect().top - 120);
      if (by <= 0) return;
      var still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollBy({ top: Math.round(by), behavior: still ? 'auto' : 'smooth' });
    }
    Array.prototype.forEach.call(el.modes, function (r) {
      r.addEventListener('change', function () { if (r.checked && mode === 'sampler') revealSummary(); });
    });

    // ---------- initial ----------
    if (el.singleFrom && data.flavors.length) {
      var minV = null;
      data.flavors.forEach(function (f) { f.variants.forEach(function (v) { if (!minV || v.price < minV.price) minV = v; }); });
      if (minV) el.singleFrom.textContent = money(minV.price, fmt);
    }
    renderMode();
  }

  function initAll() { Array.prototype.forEach.call(document.querySelectorAll('[data-mix-box]'), init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll); else initAll();
})();
